import React, { useState, useEffect } from "react";
import Select, { ActionMeta, MultiValue } from "react-select";
import useErrorHandler from "@beeldit/core/errors/useErrorHandler";
import index from "@beeldit/core/services";
import { t } from "i18next";
import { Producer } from "../models/Producer";

interface Option {
  label: string;
  value: number | string;
}

function MultiSelector(props: any) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    onChange,
    maxItems,
    placeholder,
    producerId, // nuevo
  } = props;

  const [selectedOptions, setSelectedOptions] = useState<MultiValue<Option>>(
    []
  );
  const [productSelectedOptions, setProductSelectedOptions] = useState<
    MultiValue<Option>
  >([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [elementsData, setElementsData] = useState({ elements: [] });
  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [productElementsData, setProductElementsData] = useState({
    elements: [],
  });

  const errorHandler = useErrorHandler();

  // Etiqueta del formato: "Nombre del producto principal - variante" para que
  // se identifique con claridad (la combinación sola solo muestra la variante).
  const buildCombinationLabel = (combination: any): string => {
    const productName =
      combination?.product?.name?.es || combination?.product?.name;
    const variantName = combination?.name?.es || combination?.name;
    if (productName && variantName) return `${productName} - ${variantName}`;
    return productName || variantName || "";
  };

  // Cargar productores (solo si no hay producerId por props)
  useEffect(() => {
    if (!producerId) {
      index("producers", "relations=user")
        .then((response: any) => {
          setElementsData(response.data);
        })
        .catch((error: any) => {
          errorHandler(error, null);
        });
    }
  }, [producerId]);

  // Establecer selección del productor automáticamente si viene por props.
  // Conservamos las combinaciones que ya trae el modelo (caso edición) para no
  // vaciar "Aplicado a" al fijar el productor por defecto.
  useEffect(() => {
    if (producerId) {
      const newSelected = [
        { value: producerId, label: `Producer ${producerId}` },
      ];
      setSelectedOptions(newSelected);
      const existingProducts = (value ?? [])
        .filter((v: any) => v.type === "combination")
        .map((v: any) => ({ value: v.id, label: `${v.id}` }));
      updateModel(newSelected, existingProducts);
    }
  }, [producerId]);

  // Cargar combinaciones del/los productor(es)
  useEffect(() => {
    const producerIds = producerId
      ? producerId
      : selectedOptions.map((opt) => opt.value).join(",");

    index("combinations", `relations=product&producer_ids=${producerIds}`)
      .then((response: any) => {
        setProductElementsData(response.data);

        const newProductOptions = response.data.elements.map(
          (combination: any) => ({
            label: buildCombinationLabel(combination),
            value: combination.id,
          })
        );

        setProductOptions(newProductOptions);

        const validSelected = productSelectedOptions.filter((selected) =>
          newProductOptions.some((opt: any) => opt.value === selected.value)
        );
        setProductSelectedOptions(validSelected);

        // updateModel(selectedOptions, validSelected);
      })
      .catch((error: any) => {
        errorHandler(error, null);
      });
  }, [selectedOptions]);

  // Mapear opciones de productores y preseleccionarlos si ya están en el modelo
  useEffect(() => {
    const mappedOptions = elementsData.elements.map((producer: Producer) => ({
      label: producer.user.name,
      value: producer.id,
    }));
    setOptions(mappedOptions);

    const currentSelected = mappedOptions.filter((option) =>
      value?.some((v: any) => v.id === option.value && v.type === "producer")
    );
    setSelectedOptions(currentSelected);
  }, [elementsData, value]);

  // Mapear opciones y selección de productos
  useEffect(() => {
    const mappedProductOptions = productElementsData.elements.map(
      (combination: any) => ({
        label: buildCombinationLabel(combination),
        value: combination.id,
      })
    );
    setProductOptions(mappedProductOptions);

    const currentSelected = mappedProductOptions.filter((option) =>
      value?.some((v: any) => v.id === option.value && v.type === "combination")
    );
    setProductSelectedOptions(currentSelected);
  }, [productElementsData, value]);

  const updateModel = (
    newProducers: MultiValue<Option>,
    newProducts: MultiValue<Option>
  ) => {
    // En modo productor el productor es fijo (viene por props) y su selector no
    // se muestra; lo forzamos siempre para que no se caiga de couponables al
    // cambiar los productos (el backend exige el productor junto a la combinación).
    const producerValues = producerId
      ? [{ id: producerId, type: "producer" }]
      : newProducers.map((option) => ({
          id: option.value,
          type: "producer",
        }));
    const productValues = newProducts.map((option) => ({
      id: option.value,
      type: "combination",
    }));
    onChange([...producerValues, ...productValues]);
  };

  const handleProducerChange = (
    newValue: MultiValue<Option>,
    actionMeta: ActionMeta<Option>
  ) => {
    if (
      actionMeta.action === "clear" ||
      actionMeta.action === "remove-value" ||
      (actionMeta.action === "select-option" &&
        (!maxItems || newValue.length <= maxItems))
    ) {
      setSelectedOptions(newValue);
      updateModel(newValue, productSelectedOptions);
    }
  };

  const handleProductChange = (
    newValue: MultiValue<Option>,
    actionMeta: ActionMeta<Option>
  ) => {
    if (
      actionMeta.action === "clear" ||
      actionMeta.action === "remove-value" ||
      (actionMeta.action === "select-option" &&
        (!maxItems || newValue.length <= maxItems))
    ) {
      setProductSelectedOptions(newValue);
      updateModel(selectedOptions, newValue);
    }
  };

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: "lightgray",
      borderRadius: 0,
    }),
  };

  return (
    <div>
      {!producerId && (
        <div style={{ marginBottom: "1rem" }}>
          <Select
            id={`${id}-producer`}
            isMulti
            options={options}
            value={selectedOptions}
            required={required}
            isDisabled={disabled || readonly}
            onChange={handleProducerChange}
            styles={selectStyles}
            placeholder={t(placeholder || "select_producers")}
          />
        </div>
      )}

      <div style={{ marginBottom: "1rem" }}>
        <Select
          id={`${id}-product`}
          isMulti
          options={productOptions}
          value={productSelectedOptions}
          required={required}
          isDisabled={disabled || readonly}
          onChange={handleProductChange}
          styles={selectStyles}
          placeholder={t(placeholder || "Todos los productos disponibles")}
        />
      </div>
      {/* Debug JSON del modelo */}
      {/* <pre
        style={{ background: "#f8f8f8", padding: "10px", fontSize: "0.9rem" }}
      >
        {JSON.stringify(
          [
            ...selectedOptions.map((option) => ({
              id: option.value,
              type: "producer",
            })),
            ...productSelectedOptions.map((option) => ({
              id: option.value,
              type: "combination",
            })),
          ],
          null,
          2
        )}
      </pre> */}
    </div>
  );
}

function ProducerAndProductMultiSelector(props: any) {
  const { id, options, value, required, disabled, readonly, onChange } = props;
  return (
    <MultiSelector
      id={id}
      options={options.enumOptions}
      maxItems={options.maxItems}
      placeholder={options.placeholder}
      value={value}
      required={required}
      disabled={disabled}
      readonly={readonly}
      onChange={onChange}
      producerId={options.producerId} // ← importante
    />
  );
}

export default ProducerAndProductMultiSelector;
